import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import "../styles/profile.css";

const API_BASE_URL = "https://primerise.space";

const PROFILE_DEFAULTS = {
  fullName: "",
  email: "",
  phone: "",
  country: "",
  city: "",
  dateOfBirth: "",
};

export default function Profile() {
  const navigate = useNavigate();

  const [profile, setProfile] = useState(PROFILE_DEFAULTS);
  const [startingKyc, setStartingKyc] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [kycStatus, setKycStatus] = useState("unverified");
  const [isLocked, setIsLocked] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [kycStep, setKycStep] = useState(1);

  const [idFrontFile, setIdFrontFile] = useState(null);
  const [idBackFile, setIdBackFile] = useState(null);
  const [selfieFile, setSelfieFile] = useState(null);

  const [idFrontPreview, setIdFrontPreview] = useState("");
  const [idBackPreview, setIdBackPreview] = useState("");
  const [selfiePreview, setSelfiePreview] = useState("");

  const getToken = () => {
    return (
      localStorage.getItem("token") ||
      localStorage.getItem("primeriseToken") ||
      localStorage.getItem("naxoraToken") ||
      ""
    );
  };

  const getAuthHeaders = (includeJson = false) => {
    const token = getToken();
    const headers = {};

    if (includeJson) {
      headers["Content-Type"] = "application/json";
    }

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return headers;
  };

  const normalizeStatus = (status) => {
    return String(status || "unverified").toLowerCase();
  };

  const formatKycStatus = (status) => {
    const value = normalizeStatus(status);

    if (value === "verified") return "Verified";
    if (value === "pending") return "Pending";
    if (value === "rejected") return "Rejected";
    return "Unverified";
  };

  const checkLockStatus = (data) => {
    return Boolean(
      String(data.phone || "").trim() &&
        String(data.country || "").trim() &&
        String(data.city || "").trim() &&
        String(data.dateOfBirth || "").trim()
    );
  };

  const validateProfileDetails = () => {
    if (!profile.fullName.trim()) {
      throw new Error("Full name is required");
    }

    if (!profile.email.trim()) {
      throw new Error("Email is required");
    }

    if (!profile.phone.trim()) {
      throw new Error("Phone is required");
    }

    if (!profile.country.trim()) {
      throw new Error("Country is required");
    }

    if (!profile.city.trim()) {
      throw new Error("City is required");
    }

    if (!profile.dateOfBirth.trim()) {
      throw new Error("Date of birth is required");
    }
  };

  const validateDocumentImages = () => {
    if (!idFrontFile && !idFrontPreview) {
      throw new Error("National ID front image is required");
    }

    if (!idBackFile && !idBackPreview) {
      throw new Error("National ID back image is required");
    }
  };

  const validateSelfieImage = () => {
    if (!selfieFile && !selfiePreview) {
      throw new Error("Selfie / face verification image is required");
    }
  };

  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const readStoredJson = (key) => {
    try {
      return JSON.parse(localStorage.getItem(key)) || {};
    } catch {
      return {};
    }
  };

  const getSafeUserForStorage = (userData) => {
    return {
      id: userData.id || "",
      name: userData.name || userData.fullName || "",
      fullName: userData.fullName || userData.name || "",
      email: userData.email || "",
      phone: userData.phone || "",
      country: userData.country || "",
      city: userData.city || "",
      dateOfBirth: userData.dateOfBirth || "",
      balance: Number(userData.balance || 0),
      lockedBalance: Number(userData.lockedBalance || 0),
      totalProfit: Number(userData.totalProfit || 0),
      kycStatus: userData.kycStatus || "unverified",
      rejectionReason: userData.rejectionReason || "",
      isAdmin: userData.isAdmin === true,
      isActive: userData.isActive !== false,
      myReferralCode: userData.myReferralCode || userData.referralCode || "",
      referralCode: userData.referralCode || userData.myReferralCode || "",
      referredBy: userData.referredBy || "",
      createdAt: userData.createdAt || "",
    };
  };

  const safeSetLocalStorage = (key, value) => {
    try {
      localStorage.setItem(key, value);
      return true;
    } catch (error) {
      console.error(`Local storage save failed for ${key}:`, error);
      return false;
    }
  };

  const clearLargeUserStorageFields = () => {
    const keys = ["user", "currentUser", "primeriseCurrentUser", "naxoraCurrentUser"];

    keys.forEach((key) => {
      const storedUser = readStoredJson(key);
      const safeUser = getSafeUserForStorage(storedUser);
      safeSetLocalStorage(key, JSON.stringify(safeUser));
    });
  };

  const clearAuthStorage = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("primeriseToken");
    localStorage.removeItem("naxoraToken");
    localStorage.removeItem("primeriseAuth");
    localStorage.removeItem("naxoraAuth");
    localStorage.removeItem("isAuthenticated");
    localStorage.removeItem("primeriseCurrentUser");
    localStorage.removeItem("naxoraCurrentUser");
    localStorage.removeItem("currentUser");
    localStorage.removeItem("user");
  };

  const handleUnauthorized = async () => {
    await Swal.fire({
      icon: "error",
      title: "Unauthorized",
      text: "Please login again",
      confirmButtonColor: "#d4af37",
      background: "#0b0b0b",
      color: "#ffffff",
    });

    clearAuthStorage();
    window.location.replace("/login");
  };

  const persistUserLocally = (userData) => {
    const safeUser = getSafeUserForStorage(userData);
    const safePayload = JSON.stringify(safeUser);

    const savedUser = safeSetLocalStorage("user", safePayload);
    const savedCurrentUser = safeSetLocalStorage("currentUser", safePayload);
    const savedPrimeriseUser = safeSetLocalStorage("primeriseCurrentUser", safePayload);
    const savedNaxoraUser = safeSetLocalStorage("naxoraCurrentUser", safePayload);

    if (!savedUser || !savedCurrentUser || !savedPrimeriseUser || !savedNaxoraUser) {
      clearLargeUserStorageFields();
    }
  };

  const resetKycFilesForRejected = () => {
    setIdFrontFile(null);
    setIdBackFile(null);
    setSelfieFile(null);
    setIdFrontPreview("");
    setIdBackPreview("");
    setSelfiePreview("");
    setKycStep(1);
  };

  const fetchProfileAndKyc = async () => {
    try {
      setLoadingProfile(true);

      const token = getToken();

      if (!token) {
        await handleUnauthorized();
        return;
      }

      clearLargeUserStorageFields();

      const response = await fetch(`${API_BASE_URL}/api/kyc/me`, {
        method: "GET",
        headers: getAuthHeaders(),
      });

      let data = {};
      try {
        data = await response.json();
      } catch {
        data = {};
      }

      if (response.status === 401) {
        await handleUnauthorized();
        return;
      }

      if (!response.ok) {
        throw new Error(data.message || "Failed to load profile");
      }

      const profileData = data.profile || {};
      const kycData = data.kyc || null;

      const storedUser = readStoredJson("user");
      const storedCurrentUser = readStoredJson("currentUser");
      const storedPrimeriseUser = readStoredJson("primeriseCurrentUser");
      const storedNaxoraUser = readStoredJson("naxoraCurrentUser");

      const mergedUser = {
        ...storedUser,
        ...storedCurrentUser,
        ...storedPrimeriseUser,
        ...storedNaxoraUser,
        ...profileData,
      };

      const nextProfile = {
        fullName:
          profileData.fullName || profileData.name || mergedUser.name || "",
        email: profileData.email || mergedUser.email || "",
        phone: profileData.phone || mergedUser.phone || "",
        country: profileData.country || mergedUser.country || "",
        city: profileData.city || mergedUser.city || "",
        dateOfBirth: profileData.dateOfBirth || mergedUser.dateOfBirth || "",
      };

      const nextStatus =
        profileData.kycStatus || mergedUser.kycStatus || "unverified";

      setProfile(nextProfile);
      setKycStatus(nextStatus);
      setRejectionReason(kycData?.rejectionReason || "");

      if (normalizeStatus(nextStatus) === "rejected") {
        resetKycFilesForRejected();
      } else {
        setIdFrontPreview((kycData && kycData.idFrontUrl) || "");
        setIdBackPreview((kycData && kycData.idBackUrl) || "");
        setSelfiePreview((kycData && kycData.selfieUrl) || "");
      }

      setIsLocked(checkLockStatus(nextProfile));

      persistUserLocally({
        ...mergedUser,
        ...nextProfile,
        name: nextProfile.fullName,
        fullName: nextProfile.fullName,
        email: nextProfile.email,
        phone: nextProfile.phone,
        country: nextProfile.country,
        city: nextProfile.city,
        dateOfBirth: nextProfile.dateOfBirth,
        kycStatus: nextStatus,
        rejectionReason: kycData?.rejectionReason || "",
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Failed",
        text: error.message || "Unable to load profile",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });
    } finally {
      setLoadingProfile(false);
    }
  };

  useEffect(() => {
    fetchProfileAndKyc();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setProfile((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageSelect = (e, type) => {
    const file = e.target.files && e.target.files[0];

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      Swal.fire({
        icon: "warning",
        title: "Invalid File",
        text: "Please select an image file",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });
      return;
    }

    const previewUrl = URL.createObjectURL(file);

    if (type === "front") {
      setIdFrontFile(file);
      setIdFrontPreview(previewUrl);
    }

    if (type === "back") {
      setIdBackFile(file);
      setIdBackPreview(previewUrl);
    }

    if (type === "selfie") {
      setSelfieFile(file);
      setSelfiePreview(previewUrl);
    }
  };

  const goToNextKycStep = () => {
    try {
      if (kycStep === 1) {
        validateProfileDetails();
      }

      if (kycStep === 2) {
        validateDocumentImages();
      }

      setKycStep((prev) => Math.min(prev + 1, 3));
    } catch (error) {
      Swal.fire({
        icon: "warning",
        title: "Required Information",
        text: error.message || "Please complete the required fields",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });
    }
  };

  const goToPreviousKycStep = () => {
    setKycStep((prev) => Math.max(prev - 1, 1));
  };

  const handleStartKyc = async () => {
    try {
      validateProfileDetails();
      validateDocumentImages();
      validateSelfieImage();

      const currentStatus = normalizeStatus(kycStatus);

      if (currentStatus === "pending") {
        throw new Error("Your KYC is already pending review");
      }

      if (currentStatus === "verified") {
        throw new Error("Your KYC is already verified");
      }

      setStartingKyc(true);

      const token = getToken();

      if (!token) {
        await handleUnauthorized();
        return;
      }

      const idFront =
        idFrontFile instanceof File
          ? await fileToBase64(idFrontFile)
          : idFrontPreview;

      const idBack =
        idBackFile instanceof File
          ? await fileToBase64(idBackFile)
          : idBackPreview;

      const selfie =
        selfieFile instanceof File
          ? await fileToBase64(selfieFile)
          : selfiePreview;

      const response = await fetch(`${API_BASE_URL}/api/kyc/submit`, {
        method: "POST",
        headers: getAuthHeaders(true),
        body: JSON.stringify({
          fullName: profile.fullName.trim(),
          email: profile.email.trim(),
          phone: profile.phone.trim(),
          country: profile.country.trim(),
          city: profile.city.trim(),
          dateOfBirth: profile.dateOfBirth,
          idFront,
          idBack,
          selfie,
        }),
      });

      let data = {};
      try {
        data = await response.json();
      } catch {
        data = {};
      }

      if (response.status === 401) {
        await handleUnauthorized();
        return;
      }

      if (!response.ok) {
        throw new Error(data.message || "Unable to submit KYC");
      }

      setKycStatus("pending");
      setRejectionReason("");
      setIsLocked(true);
      setKycStep(1);

      const updatedUser = {
        ...readStoredJson("user"),
        ...profile,
        name: profile.fullName,
        fullName: profile.fullName,
        email: profile.email,
        phone: profile.phone,
        country: profile.country,
        city: profile.city,
        dateOfBirth: profile.dateOfBirth,
        kycStatus: "pending",
        rejectionReason: "",
      };

      persistUserLocally(updatedUser);

      if (data.kyc) {
        setIdFrontPreview(data.kyc.idFrontUrl || "");
        setIdBackPreview(data.kyc.idBackUrl || "");
        setSelfiePreview(data.kyc.selfieUrl || "");
      }

      setIdFrontFile(null);
      setIdBackFile(null);
      setSelfieFile(null);

      Swal.fire({
        icon: "success",
        title: "KYC Submitted",
        text: "Your verification request has been submitted successfully",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Failed",
        text: error.message || "Unable to submit KYC",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });
    } finally {
      setStartingKyc(false);
    }
  };

  const currentStatus = normalizeStatus(kycStatus);
  const showKycWizard =
    currentStatus === "unverified" || currentStatus === "rejected";
  const showRetryNote = currentStatus === "rejected";
  const showPendingNote = currentStatus === "pending";
  const showVerifiedNote = currentStatus === "verified";

  const submitButtonText =
    currentStatus === "rejected"
      ? startingKyc
        ? "Submitting..."
        : "Submit KYC Again"
      : startingKyc
      ? "Submitting..."
      : "Submit KYC";

  const detailsEditable = !isLocked || currentStatus === "rejected";

  if (loadingProfile) {
    return (
      <div className="profile-page">
        <div className="profile-wrapper">
          <div className="profile-header">
            <button
              type="button"
              className="profile-back-btn"
              onClick={() => navigate("/dashboard")}
            >
              Back
            </button>
          </div>

          <div className="profile-loading-state">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <div className="profile-wrapper">
        <div className="profile-header">
          <button
            type="button"
            className="profile-back-btn"
            onClick={() => navigate("/dashboard")}
          >
            Back
          </button>
        </div>

        <div className="profile-main-card">
          <div className="profile-card-head">
            <h2>KYC Verification</h2>
            <p>
              Complete your identity verification to unlock trading and
              withdrawal features.
            </p>
          </div>

          <div className="kyc-status-box">
            <span className="kyc-status-label">KYC Status</span>
            <strong className={`kyc-status-value ${currentStatus}`}>
              {formatKycStatus(currentStatus)}
            </strong>
          </div>

          {showRetryNote ? (
            <div className="kyc-note-box rejected">
              <p>
                {rejectionReason
                  ? `Your KYC was rejected. Reason: ${rejectionReason}`
                  : "Your KYC was rejected. Please complete all steps again."}
              </p>
            </div>
          ) : null}

          {showPendingNote ? (
            <div className="kyc-result-card pending">
              <div className="kyc-result-icon">!</div>
              <h3>Verification Pending</h3>
              <p>
                Your KYC is pending admin review. Upload fields are hidden until
                a decision is made.
              </p>
            </div>
          ) : null}

          {showVerifiedNote ? (
            <div className="kyc-result-card verified">
              <div className="kyc-result-icon">✓</div>
              <h3>KYC Verified</h3>
              <p>
                Your KYC has been verified successfully. Trading and withdrawal
                access is enabled.
              </p>
            </div>
          ) : null}

          {showKycWizard ? (
            <>
              <div className="kyc-stepper">
                <div className={`kyc-step ${kycStep >= 1 ? "active" : ""}`}>
                  <span>1</span>
                  <strong>Details</strong>
                </div>

                <div className={`kyc-step ${kycStep >= 2 ? "active" : ""}`}>
                  <span>2</span>
                  <strong>Documents</strong>
                </div>

                <div className={`kyc-step ${kycStep >= 3 ? "active" : ""}`}>
                  <span>3</span>
                  <strong>Selfie</strong>
                </div>
              </div>

              {kycStep === 1 ? (
                <div className="kyc-step-card">
                  <h3>Step 1: Personal Details</h3>
                  <p>
                    Enter accurate details. These details are required before
                    uploading documents.
                  </p>

                  <div className="profile-form-grid">
                    <div className="profile-form-group">
                      <label>Full Name</label>
                      <input
                        type="text"
                        name="fullName"
                        value={profile.fullName}
                        readOnly
                      />
                    </div>

                    <div className="profile-form-group">
                      <label>Email</label>
                      <input
                        type="email"
                        name="email"
                        value={profile.email}
                        readOnly
                      />
                    </div>

                    <div className="profile-form-group">
                      <label>Phone</label>
                      <input
                        type="text"
                        name="phone"
                        value={profile.phone}
                        onChange={handleChange}
                        placeholder="Enter phone number"
                        disabled={!detailsEditable}
                      />
                    </div>

                    <div className="profile-form-group">
                      <label>Date of Birth</label>
                      <input
                        type="date"
                        name="dateOfBirth"
                        value={profile.dateOfBirth}
                        onChange={handleChange}
                        disabled={!detailsEditable}
                      />
                    </div>

                    <div className="profile-form-group">
                      <label>Country</label>
                      <input
                        type="text"
                        name="country"
                        value={profile.country}
                        onChange={handleChange}
                        placeholder="Enter country"
                        disabled={!detailsEditable}
                      />
                    </div>

                    <div className="profile-form-group">
                      <label>City</label>
                      <input
                        type="text"
                        name="city"
                        value={profile.city}
                        onChange={handleChange}
                        placeholder="Enter city"
                        disabled={!detailsEditable}
                      />
                    </div>
                  </div>

                  <button
                    type="button"
                    className="kyc-start-btn"
                    onClick={goToNextKycStep}
                  >
                    Continue to Documents
                  </button>
                </div>
              ) : null}

              {kycStep === 2 ? (
                <div className="kyc-step-card">
                  <h3>Step 2: Document Pictures</h3>
                  <p>
                    Upload clear front and back images of your national ID card.
                  </p>

                  <div className="kyc-upload-grid">
                    <div className="kyc-upload-card">
                      <label className="kyc-upload-label">ID Card Front</label>
                      <label className="kyc-upload-btn">
                        Upload Image
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleImageSelect(e, "front")}
                          hidden
                        />
                      </label>
                      {idFrontPreview ? (
                        <img
                          src={idFrontPreview}
                          alt="ID Front"
                          className="kyc-preview-image"
                        />
                      ) : (
                        <div className="kyc-preview-placeholder">
                          No image selected
                        </div>
                      )}
                    </div>

                    <div className="kyc-upload-card">
                      <label className="kyc-upload-label">ID Card Back</label>
                      <label className="kyc-upload-btn">
                        Upload Image
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleImageSelect(e, "back")}
                          hidden
                        />
                      </label>
                      {idBackPreview ? (
                        <img
                          src={idBackPreview}
                          alt="ID Back"
                          className="kyc-preview-image"
                        />
                      ) : (
                        <div className="kyc-preview-placeholder">
                          No image selected
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="kyc-step-actions">
                    <button
                      type="button"
                      className="kyc-secondary-btn"
                      onClick={goToPreviousKycStep}
                    >
                      Back
                    </button>

                    <button
                      type="button"
                      className="kyc-start-btn"
                      onClick={goToNextKycStep}
                    >
                      Continue to Selfie
                    </button>
                  </div>
                </div>
              ) : null}

              {kycStep === 3 ? (
                <div className="kyc-step-card">
                  <h3>Step 3: Selfie Verification</h3>
                  <p>
                    Take a clear selfie for face verification and submit your
                    KYC request.
                  </p>

                  <div className="kyc-upload-grid single">
                    <div className="kyc-upload-card">
                      <label className="kyc-upload-label">
                        Face Verification
                      </label>
                      <label className="kyc-upload-btn">
                        Open Camera
                        <input
                          type="file"
                          accept="image/*"
                          capture="user"
                          onChange={(e) => handleImageSelect(e, "selfie")}
                          hidden
                        />
                      </label>
                      {selfiePreview ? (
                        <img
                          src={selfiePreview}
                          alt="Face Verification"
                          className="kyc-preview-image"
                        />
                      ) : (
                        <div className="kyc-preview-placeholder">
                          No image selected
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="kyc-step-actions">
                    <button
                      type="button"
                      className="kyc-secondary-btn"
                      onClick={goToPreviousKycStep}
                    >
                      Back
                    </button>

                    <button
                      type="button"
                      className="kyc-start-btn"
                      onClick={handleStartKyc}
                      disabled={startingKyc}
                    >
                      {submitButtonText}
                    </button>
                  </div>
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}